<template>
    <main id="compare-machine-selection" :class="{'header-embed': embed}">
        <div class="machine-selection-header" v-if="embed">
          <h1 class="title-with-icon">
            <span class="icon-compare" :class="brandColor"></span>
            {{ $t("i_compare") }}
          </h1>

          <template>
            <div class="tabs" v-if="!isPhone">
                          <span
                              v-for="tab in tabs"
                              :key="tab.code"
                              class="tab"
                              :class="{ active: selectedTab === tab.code }"
                              :data-tracking="tab.code === 'compare' ? 'view_product_compare_page' : ''"
                              @click="switchTab(tab.code)"
                          >
                              {{ tab.label }}
                          </span>
            </div>
            <b-select
                v-else
                v-model="selectedTab"
                class="select-tab"
                size="is-large"
                rounded
                expanded
                @input="switchTab($event)"
            >
              <option
                  v-for="tab in tabs"
                  :key="tab.code"
                  :value="tab.code"
                  :data-tracking="tab.code === 'compare' ? 'view_product_compare_page' : ''"
              >
                {{ tab.label }}
              </option>
            </b-select>
          </template>
        </div>
        <div class="columns is-marginless">
            <div class="column left-column" :class="{'is-two-thirds': !embed, 'is-half': embed}">
                <h1 class="title-with-icon" v-if="!embed">
                  <span class="icon-compare" :class="brandColor"></span>
                  {{ $t("i_compare") }}
                </h1>

                <div class="step" :class="brandColor">
                    {{ $tc('step', currentStep+'/2') }}
                </div>

                <div class="step-subtitle">
                    <template v-if="currentStep === 1">
                        {{ $t("please_select_reference_machine") }}
                    </template>
                    <template v-else>
                        {{ $tc("please_select_up_to_n_machines", 3) }}
                    </template>
                </div>

                <p class="subtitle-colored-text from-all-machines" :class="brandColor" v-if="currentStep === 2">
                    {{ $t("among_all_machines") }}
                </p>
                <div class="machine-selection-autocomplete">
                    <autocomplete-machine-comparison :prefill-data="engine" :selected-products="machinesToCompare" @productSelected="productSelected($event)" />
                </div>

                <template v-if="currentStep === 2 && machineCompetitors.length > 0">
                    <p class="subtitle-colored-text from-competitors" :class="brandColor">
                        {{ $t("among_competitors") }}
                    </p>
                    <div class="competitors" :class="{'scrollable': scrollable}">
                        <div class="machine-datas" v-for="machineDatas in machineCompetitors">
                            <div class="left-part">
                                <span class="action-plus-minus-button" :class="machineDatas['icon']" @click="productSelected(machineDatas)"></span>
                            </div>
                            <div class="right-part">
                                <span class="model-name"><strong>{{ machineDatas['Model'] }}</strong></span>
                                <span class="brand-name">{{ machineDatas['Manufacturer'].toLowerCase() }}</span>
                            </div>
                        </div>
                    </div>
                    <button class="load-more-item button is-primary is-rounded is-uppercase with-small-side-padding next-step-btn is-background-color-red"
                            :class="[{'is-large': !isPhone}, {'is-small': isPhone}]"
                            @click="fetchCompetitors(count+6)">
                        <span>{{ $t("load_more_competitors") }}</span>
                    </button>
                </template>
            </div>
            <div class="column is-one-third right-column">
                <div class="your-selection-title">
                    {{ $t("your_selection") }}
                </div>
                <template v-for="index in 4">
                    <selection-box-item
                        :number="(index > 1 ? index : null)"
                        :is-base="index === 1"
                        :machine-datas="(machinesToCompare[(index-1)] ? machinesToCompare[(index-1)] : null)"
                        @productSelected="productSelected($event)"
                    />
                    <div class="versus" :class="brandColor" v-if="index === 1">
                        VS
                    </div>
                </template>
                <button
                    class="button is-primary is-large is-rounded is-uppercase with-small-side-padding next-step-btn"
                    :class="[brandBackgroundColor, {'disabled': machinesToCompare.length < 2}]"
                    @click="goToMachinesComparison()"
                >
                    <span>{{ $t('to_compare') }}</span>
                    <span class="icon-compare is-right-side"></span>
                </button>
            </div>
        </div>
    </main>
</template>

<script>
    import AutocompleteMachineComparison from "@/components/Autocomplete/AutocompleteMachineComparison.vue"
    import SelectionBoxItem from "@/components/SelectionBox/SelectionBoxItem.vue"
    import compareMixins from "@/common/mixins/compare"
    import machineService from "@/services/speccheck/machine";
    import {mapState} from "vuex";
    import speedTrackingMixins from "@/common/mixins/speedTracking";
    import {triggerTrackingEvent} from "@/common/helpers";

    export default {
        name: 'CompareMachinesSelection',
        mixins: [
          compareMixins
        ],
        props: {
            embed: {
                type: Boolean,
                required: false,
                default: false,
            },
            engine: {
                type: String,
                required: false,
                default: null,
            }
        },
        components: {
            AutocompleteMachineComparison,
            SelectionBoxItem
        },
        data() {
            return {
                machinesToCompare: [],
                machineCompetitors: [],
                scrollable: false,
                tabs: [
                    {
                        code: "counterArguments",
                        label: this.$t('counter_arguments')
                    },
                    {
                        code: "compare",
                        label: this.$t('compare_with_other_products')
                    },
                ],
                count:2,
                selectedTab: 'compare',
            }
        },
        computed: {
            ...mapState({
            isPhone: state => state.settings.isPhone,
            }),
            currentStep(){
                if(this.machinesToCompare.length > 0)
                    return 2
                else
                    return 1
            }
        },

        methods: {
            productSelected(machine){
                /*gtag('event', 'click_closest_competitors_product', {
                    'event_category': 'click',
                    'event_label': `Clic sur un produit au niveau des closest competitors dans la comparaison : ${machine['Manufacturer']} - ${machine['Model']}`
                });*/

                // triggerTrackingEvent('click_closest_competitors_product', 'click', `Clic sur un produit au niveau des closest competitors dans la comparaison : ${machine['Manufacturer']} - ${machine['Model']}`)
                let findIndex = this.machinesToCompare.findIndex(x => x['Machine_ID'] === machine['Machine_ID']);
                // The index is found and it's zero, mean that its the reference product that was selection so we clear all to start anew
                if(findIndex === 0){
                    this.machinesToCompare = [];
                    this.machineCompetitors = [];
                }
                else {
                    // If we find an index superior to zero, its a machine to compare, so we delete it from the array
                    if(findIndex !== -1){
                        this.machinesToCompare.splice(findIndex, 1)
                        this.refreshCompetitorsIcons();
                    }
                    // If index not found, it's an addition so we can push the machine in the array (if not already filled with 3 machines to compare)
                    else{
                        if(this.machinesToCompare.length !== 4){
                            this.machinesToCompare.push(machine)

                            // If it's the first machine added to the array, we fetch competitors, else we only refresh competitors icons
                            if(this.machinesToCompare.length === 1)
                                this.fetchCompetitors();
                            else
                                this.refreshCompetitorsIcons();
                        }
                        else {
                            this.$toast.open({
                                message: this.$tc('you_cannot_compare_more_than_x_machines', 4),
                                type: 'is-warning',
                                duration: 3000
                            })
                        }
                    }
                }
            },

            fetchCompetitors(count = 2){
              this.count=count;
                let referenceMachine = this.machinesToCompare[0];

                let onlyTier = null;
                if(parseInt(referenceMachine['Tier_ID']) > 0)
                    onlyTier = parseInt(referenceMachine['Tier_ID'])

                this.startLoading();
                machineService.competitors({
                    mid: referenceMachine['Variation_ID'],
                    range: parseInt(referenceMachine['Range_ID']),
                    limit: count,
                    // exmanids: 56
                })
                .then((response) => {
                    let competitors = []
                    response.data.data.forEach(machine => {
                        if(machine['Machine_ID'] !== this.machinesToCompare[0]['Machine_ID']){
                            machine['icon'] = 'plus';
                            competitors.push(machine)
                        }
                    })

                    this.machineCompetitors = competitors;
                    if (this.machineCompetitors.length > 4) {
                      this.scrollable = true;
                    }
                  this.refreshCompetitorsIcons();
                })
                .finally(() => this.stopLoading());

            },
            refreshCompetitorsIcons(){
                this.machineCompetitors.map(machineCompetitor => {
                    if(this.machinesToCompare.find(x => x['Machine_ID'] === machineCompetitor['Machine_ID']))
                        machineCompetitor['icon'] = 'minus';
                    else
                        machineCompetitor['icon'] = 'plus';
                });
            },
            goToMachinesComparison(){
                if(this.machinesToCompare.length >= 2){
                    let machinesToCompareIds = this.machinesToCompare.map(x => x['Machine_ID']);
                    this.$store.commit('compare/setMachinesToCompare', machinesToCompareIds);
                    if (this.embed) this.$emit('switch');
                    else this.$router.push({ name: 'compare-comparison' });
                }
            },
            switchTab(code) {
                // console.log(code)
                this.selectedTab = code;
                if (code === 'counterArguments') this.$emit('switch');
            }
        }
    }
</script>

<style lang="scss">
    #compare-machine-selection {
        position: relative;
        height: calc(100vh - #{$top-navigation-height});
        overflow: hidden;
        text-align: center;

        @media #{$mq_phone} {
            height: auto;
        }

        &.header-embed {
          height: auto;
          .machine-selection-header {
            margin: 60px auto 30px;
          }

          .left-column {
            justify-content: flex-start;

            .step {
              margin-top: 0;
            }
          }
        }

        .left-column {
            display: flex;
            flex-direction: column;
            height: calc(100vh - #{$top-navigation-height});
            align-items: center;
            justify-content: center;

            @media #{$mq_phone} {
                display: block;
                height: auto;
                padding-bottom: 30px;
            }

            .step {
                margin-top: 60px;
                font-size: 23px;
                font-weight: 900;
                line-height: 0.96;

                @media #{$mq_phone} {
                    font-size: 20px;
                    margin-top: 60px;
                }
            }

            .step-subtitle {
                margin-top: 20px;
                font-size: 23px;
                font-weight: 900;
                line-height: 0.96;

                @media #{$mq_phone} {
                    font-size: 20px;
                }
            }

            .subtitle-colored-text {
                font-size: 16px;
                font-weight: 700;
                letter-spacing: 1.11px;
                text-transform: uppercase;

                @media #{$mq_phone} {
                    font-size: 14px;
                }

                &.from-all-machines {
                    margin-top: 40px;
                }

                &.from-competitors {
                    margin-top: 20px;
                }
            }

            .machine-selection-autocomplete {
                margin-top: 20px;
                width: 100%;
                max-width: 600px;
                text-align: left;

                @media #{$mq_phone} {
                    margin: 10px auto 0 auto;
                }
            }

            .competitors {
                margin-top: 30px;
                padding-right: 10px;
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                max-width: 600px;
                max-height: 280px;

                &.scrollable {
                    overflow-y: scroll;
                }

                /* width */
                &::-webkit-scrollbar {
                    width: 5px;
                    height: 5px;
                }

                /* Track */
                &::-webkit-scrollbar-track {
                    background: #888;
                }

                /* Handle */
                &::-webkit-scrollbar-thumb {
                    border-radius: 5px;
                    background: #333;
                }

                /* Handle on hover */
                &::-webkit-scrollbar-thumb:hover {
                    background: #000;
                }

                @media #{$mq_phone} {
                    display: block;
                    margin-top: 20px;
                   //max-height: 35vh;
                }

                .machine-datas {
                    flex: 0 0 50%;
                    display: flex;
                    text-align: center;
                    width: 100%;
                    margin-bottom: 24px;

                    .left-part {
                        flex: 0 0 34px;
                        margin: 0 27px 0 23px;
                    }

                    .right-part {
                        flex: 1 1 auto;
                        text-align: left;

                        .model-name {
                            display: block;
                            margin-bottom: 3px;
                        }

                        .brand-name {
                            text-transform: capitalize;
                            color: $medium-grey;
                        }
                    }
                }
            }

            .load-more-item {
                margin-top: 30px;
            }
        }

        .right-column {
            display: flex;
            flex-direction: column;
            height: calc(100vh - #{$top-navigation-height});
            align-items: center;
            justify-content: center;
            background-color: $light-grey;
            padding: 0 40px;

            @media #{$mq_phone} {
                display: block;
                height: auto;
                padding-top: 20px;
                padding-bottom: 30px;
            }

            .your-selection-title {
                font-size: 23px;
                font-weight: 900;
                line-height: 0.96;
                margin-bottom: 45px;
                margin-top: 20px;
            }

            .versus {
                font-size: 30px;
                font-weight: 900;
                margin-bottom: 20px;
            }

            .next-step-btn {
                margin-top: 20px;
            }
        }


        .tabs {
          margin-top: 45px;
        }

        .select-tab {
          margin: 0 0 50px 0;

          @media #{$mq_phone} {
            margin-bottom: 15px;
          }

          .select select {
            font-size: 13px;
          }
        }
    }
</style>

